/**
 * Design Lab Init Machine
 * Single machine handling all initialization flows
 */

import { createMachine, assign } from "xstate";
import type {
  ApolloClientType,
  DesignLabInitContext,
  DesignLabInitEvent,
  ParsedUrlParams
} from "./design-lab-init.types";

// Initial context (apolloClient provided via input)
const initialContext: Omit<DesignLabInitContext, "apolloClient"> = {
  urlParams: {} as ParsedUrlParams,
  isEditMode: false,
  flowType: null,
  project: null,
  latestProject: null,
  composition: null,
  uploadSource: null,
  targetSide: "front" as const,
  showModal: false,
  modalAction: null
};

export const designLabInitMachine = createMachine({
  id: "designLabInit",
  initial: "parsing",
  types: {} as {
    context: DesignLabInitContext;
    events: DesignLabInitEvent;
    input: {
      apolloClient: ApolloClientType;
      navigate?: (path: string, options?: { replace?: boolean }) => void;
    };
  },
  context: ({ input }) => ({
    ...initialContext,
    apolloClient: input?.apolloClient,
    ...(input?.navigate && { navigate: input.navigate })
  }),

  states: {
    parsing: {
      description: "Parse URL parameters and determine flow",
      entry: "parseUrlParams",
      always: [
        {
          guard: "isEditMode",
          target: "editModeDetected",
          actions: "setEditMode"
        },
        {
          guard: "isExistingProject",
          target: "loadingProject",
          actions: "setFlowTypeExisting"
        },
        {
          guard: "isAutoCreate",
          target: "creatingProject",
          actions: "setFlowTypeCreate"
        },
        {
          target: "checkingLatestProject",
          actions: "setFlowTypeModal"
        }
      ]
    },

    editModeDetected: {
      description: "Edit mode detected - stop here, let edit mode hook handle",
      type: "final",
      entry: assign({ isEditMode: true })
    },

    loadingProject: {
      description: "Load existing project by UUID",
      invoke: {
        id: "loadProject",
        src: "loadProject",
        input: ({ context }) => ({
          uuid: context.urlParams.uuid!,
          apolloClient: context.apolloClient!,
          sample: context.urlParams.sample ?? false
        }),
        onDone: {
          target: "checkingComposition",
          actions: ["setProject", "navigateToProjectUrl"]
        },
        onError: {
          // Even if loading fails, mark as ready since Apollo cache might still work
          target: "ready",
          actions: "setError"
        }
      }
    },

    checkingLatestProject: {
      description: "Show modal and load latest project in background",
      entry: assign({ showModal: true }),
      invoke: {
        id: "loadLatestProject",
        src: "loadLatestProject",
        input: ({ context }) => ({
          apolloClient: context.apolloClient!
        }),
        onDone: {
          actions: "setLatestProject"
          // Stay in this state - don't transition
        },
        onError: {
          // Ignore error - modal still works without latest project
        }
      },
      on: {
        CONTINUE_WITH_LATEST: {
          target: "restoringProject",
          actions: assign({
            modalAction: "continue" as const,
            showModal: false
          })
        },
        START_NEW: {
          target: "creatingProject",
          actions: assign(({ event }) => ({
            modalAction: "new" as const,
            uploadSource: event.source,
            showModal: false
          }))
        },
        CLOSE_MODAL: {
          // Closing modal = continue with latest
          target: "restoringProject",
          actions: assign({
            showModal: false,
            modalAction: "continue" as const
          })
        }
      }
    },

    restoringProject: {
      description: "Restore latest project after modal",
      entry: [
        assign(({ context }) => ({
          project: context.latestProject
        })),
        "navigateToProjectUrl"
      ],
      always: [
        {
          guard: "hasProject",
          target: "checkingComposition"
        },
        {
          // No latest project, create new
          target: "creatingProject"
        }
      ]
    },

    creatingProject: {
      description: "Create new project",
      entry: "prepareProjectCreation",
      invoke: {
        id: "createProject",
        src: "createProject",
        input: ({ context }) => ({
          composition: context.composition,
          apolloClient: context.apolloClient!,
          sample: context.urlParams.sample ?? false
        }),
        onDone: [
          {
            guard: "hasUploadSource",
            target: "processingUpload",
            actions: ["setProject", "navigateToProjectUrl"]
          },
          {
            target: "ready",
            actions: ["setProject", "navigateToProjectUrl"]
          }
        ],
        onError: {
          target: "error",
          actions: "setError"
        }
      }
    },

    checkingComposition: {
      description: "Check if upload source needs to be processed",
      always: [
        {
          guard: "hasUploadSource",
          target: "processingUpload"
        },
        {
          target: "ready"
        }
      ]
    },

    processingUpload: {
      description: "Process upload workflows",
      initial: "determining",
      states: {
        determining: {
          description: "Determine upload type",
          always: [
            { guard: "isExternalUpload", target: "downloadingFiles" },
            { guard: "isLibraryUpload", target: "openingLibrary" },
            { guard: "isCanvaUpload", target: "openingCanva" },
            { guard: "isDeviceUpload", target: "waitingForFiles" },
            { target: "complete" } // No upload source
          ]
        },

        downloadingFiles: {
          description: "Download files from external URLs",
          invoke: {
            id: "downloadFiles",
            src: "downloadExternalFiles",
            input: ({ context }) => ({
              urls: context.externalUrls!,
              fileContentType: context.urlParams.fileContentType
            }),
            onDone: {
              target: "complete",
              actions: "setUploadedFiles"
            },
            onError: {
              target: "#designLabInit.error",
              actions: "setError"
            }
          }
        },

        openingLibrary: {
          description: "Design Library workflow",
          on: {
            LIBRARY_SELECTION: {
              target: "complete"
            },
            CANCEL: {
              target: "#designLabInit.ready"
            }
          }
        },

        openingCanva: {
          description: "Canva workflow",
          on: {
            CANVA_COMPLETE: {
              target: "complete"
            },
            CANCEL: {
              target: "#designLabInit.ready"
            }
          }
        },

        waitingForFiles: {
          description: "Device upload workflow - optional, can proceed without files",
          // Machine auto-proceeds after 100ms for machine to be in ready state so canSave can run
          after: {
            100: {
              target: "complete"
            }
          },
          on: {
            FILES_SELECTED: {
              target: "complete",
              actions: "setUploadedFiles"
            },
            CANCEL: {
              target: "#designLabInit.ready"
            }
          }
        },

        complete: {
          type: "final"
        }
      },

      onDone: {
        target: "ready",
        actions: [
          assign({
            uploadSource: null // Clear upload source when workflow completes
          }),
          "navigateToProjectUrl"
        ]
      }
    },

    ready: {
      description: "Initialization complete",
      type: "final"
    },

    error: {
      description: "Error state with retry capability",
      on: {
        RETRY: {
          target: "parsing",
          actions: "clearError"
        },
        RESET: {
          target: "parsing",
          actions: "resetContext"
        }
      }
    }
  }
});
